/* Product-page recommendations from the catalog's precomputed product vectors. */
(function () {
  'use strict';

  var API_URL = '/apps/wallview/api/product-related';
  var CACHE_KEY = 'muralls:discovery:similar:v3';
  var CACHE_TTL = 24 * 60 * 60 * 1000;
  var VISUAL_COUNT = 6;
  var pendingMatches = new WeakMap();

  function storage() {
    try { return window.localStorage; } catch (error) { return null; }
  }

  function readCache() {
    var target = storage();
    if (!target) return {};
    try { return JSON.parse(target.getItem(CACHE_KEY) || '{}') || {}; } catch (error) { return {}; }
  }

  function writeCache(cache) {
    var target = storage();
    if (!target) return;
    try { target.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (error) {}
  }

  function normalizeProduct(item, index) {
    return {
      id: String(item.id || ''),
      handle: String(item.handle || ''),
      title: String(item.title || ''),
      image: String(item.image || ''),
      url: String(item.url || ''),
      productType: String(item.productType || ''),
      score: Number(item.score || 0),
      rank: index + 1,
    };
  }

  function cachedMatches(root) {
    var cache = readCache();
    var entry = cache[root.dataset.visualRelatedHandle];
    if (!entry || Date.now() - Number(entry.at || 0) > CACHE_TTL || !Array.isArray(entry.products)) return null;
    if (!entry.products.some(function (item) { return item && item.handle && item.image && item.title; })) return null;
    return entry.products;
  }

  function productsForType(payload, productType) {
    var groupName = productType.toLowerCase() === 'wall mural' ? 'wallMurals' : 'wallpapers';
    var group = payload && payload.groups && Array.isArray(payload.groups[groupName])
      ? payload.groups[groupName]
      : null;
    var products = group || (Array.isArray(payload && payload.products) ? payload.products : []);
    return products.filter(function (product) {
      return String(product.productType || '').toLowerCase() === productType.toLowerCase();
    }).map(normalizeProduct);
  }

  function fetchIndexedMatches(root) {
    var productId = String(root.dataset.productId || '').trim();
    if (!productId) return Promise.resolve([]);
    var endpoint = new URL(API_URL, window.location.origin);
    endpoint.searchParams.set('product_id', productId);
    endpoint.searchParams.set('product_type', root.dataset.visualRelatedType || '');
    endpoint.searchParams.set('limit', String(Math.max(2, Number(root.dataset.visualRelatedLimit || VISUAL_COUNT))));
    return fetch(endpoint.toString(), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    }).then(function (response) {
      if (!response.ok) throw new Error('visual_related_' + response.status);
      return response.json();
    }).then(function (payload) {
      return productsForType(payload, root.dataset.visualRelatedType || '');
    });
  }

  function fetchMatches(root) {
    var cached = cachedMatches(root);
    if (cached) return Promise.resolve(cached);
    return fetchIndexedMatches(root).then(function (products) {
      var cache = readCache();
      cache[root.dataset.visualRelatedHandle] = { at: Date.now(), products: products };
      writeCache(cache);
      return products;
    });
  }

  function prefetchedMatches(root) {
    if (!pendingMatches.has(root)) {
      var request = fetchMatches(root);
      request.catch(function () {});
      pendingMatches.set(root, request);
    }
    return pendingMatches.get(root);
  }

  function fetchProductCard(product) {
    var productUrl = new URL(product.url || '/products/' + encodeURIComponent(product.handle), window.location.origin);
    productUrl.searchParams.set('view', 'recommendation-card');
    return fetch(productUrl.toString(), { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('visual_card_' + response.status);
      return response.text();
    }).then(function (html) {
      var template = document.createElement('template');
      template.innerHTML = html.trim();
      if (!template.content.querySelector('.card-wrapper')) throw new Error('visual_card_empty');
      var item = document.createElement('li');
      item.className = 'grid__item visual-related-product';
      item.dataset.visualRelatedHandle = product.handle;
      item.appendChild(template.content.cloneNode(true));
      return item;
    }).catch(function () {
      return null;
    });
  }

  function showFallback(root) {
    root.dataset.visualRelatedReady = '1';
    root.dataset.visualRelatedState = 'fallback';
  }

  function enhance(root) {
    if (root.dataset.visualRelatedEnabled !== 'true' || root.dataset.visualRelatedReady === '1') return;
    var grid = root.querySelector('ul.product-grid');
    if (!grid || !grid.children.length) return;
    root.dataset.visualRelatedReady = '1';
    root.dataset.visualRelatedState = 'loading';

    var fallbackTimer = window.setTimeout(function () {
      if (root.dataset.visualRelatedState === 'loading') showFallback(root);
    }, 12000);

    var limit = Math.max(2, Number(root.dataset.visualRelatedLimit || 6));
    var currentHandle = root.dataset.visualRelatedHandle || '';
    var currentType = String(root.dataset.visualRelatedType || '').toLowerCase();
    prefetchedMatches(root).then(function (products) {
      if (root.dataset.visualRelatedState === 'fallback') return [];
      var visualProducts = [];
      var used = new Set([currentHandle]);
      products.forEach(function (product) {
        if (visualProducts.length >= Math.min(VISUAL_COUNT, limit)) return;
        if (!product.handle || used.has(product.handle)) return;
        if (String(product.productType || '').toLowerCase() !== currentType) return;
        used.add(product.handle);
        visualProducts.push(product);
      });
      if (!visualProducts.length) return [];
      return Promise.all(visualProducts.map(fetchProductCard));
    }).then(function (visualCards) {
      if (root.dataset.visualRelatedState === 'fallback') return;
      if (!visualCards || !visualCards.length) {
        window.clearTimeout(fallbackTimer);
        showFallback(root);
        return;
      }
      visualCards = visualCards.filter(Boolean);
      if (!visualCards.length) {
        window.clearTimeout(fallbackTimer);
        showFallback(root);
        return;
      }
      grid.replaceChildren.apply(grid, visualCards);
      window.clearTimeout(fallbackTimer);
      root.dataset.visualRelatedApplied = '1';
      root.dataset.visualRelatedState = 'applied';
      document.dispatchEvent(new CustomEvent('muralls:visual-related-applied', {
        detail: { visual: visualCards.length, discovery: 0 },
      }));
    }).catch(function (error) {
      window.clearTimeout(fallbackTimer);
      showFallback(root);
      console.warn('Muralls visual recommendations unavailable:', error);
    });
  }

  function watch(root) {
    prefetchedMatches(root);
    enhance(root);
    if (root.dataset.visualRelatedReady === '1') return;
    var observer = new MutationObserver(function () {
      enhance(root);
      if (root.dataset.visualRelatedReady === '1') observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function initialize() {
    document.querySelectorAll('product-recommendations[data-visual-related-enabled="true"]').forEach(watch);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
